import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import * as _ from "lodash";
import * as Sentry from "@sentry/react";

import { Swiper, SwiperSlide } from "swiper/react";
import { Skeleton } from "antd";
// Import Swiper styles
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/pagination";
import "@/assets/css/store-carousel.css";

// import required modules
import { Grid, Pagination } from "swiper/modules";

import "@/assets/css/swiper-cards.css";

import {
  useListaProductosMasVendidosByAddress,
  useListaComunasYMaquinasPorProductoNombre,
} from "@/hooks/useProductos";
import { useListaEmpresasMutation } from "@/hooks/useEmpresas";
import { useAuth } from "@/_zustand/auth";
import { formatMoney, getDistance } from "@/lib";

import { StoresSwiper } from "./StoresSwiper";
import { ModalProductDetail } from "../featured-products/components/ModalProductDetail";

export const BestSeller = () => {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const { mutateAsync: fetchEmpresas, data: empresasData } =
    useListaEmpresasMutation();
  const { userAddress } = useAuth();
  const { mutateAsync } = useListaProductosMasVendidosByAddress();
  const { mutateAsync: mutateAsyncMaquinas } =
    useListaComunasYMaquinasPorProductoNombre();
  const [allProducts, setAllProducts] = useState([]); // Almacena todos los productos cargados
  const [productosMasVendidos, setProductosMasVendidos] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedTiendaId, setSelectedTiendaId] = useState(null);
  const [, setComunas] = useState([]);
  const [maquinas, setMaquinas] = useState([]);
  const [isLoadingMaquinas, setIsLoadingMaquinas] = useState(false);
  const itemRef = useRef(null);
  const storeRef = useRef(null);
  const requestIdRef = useRef(0);
  const [latitude, setLatitude] = useState(-33.43695899222021);
  const [longitude, setLongitude] = useState(-70.63442929100255);
  const [, setErrorGeo] = useState(null);
  const [, setLoading] = useState(true);

  const processAndSortProducts = (data, userLat, userLon, getDistance, _) => {
    if (!data || !Array.isArray(data)) return [];

    // 1. Eliminar duplicados primero
    const uniqueData = _.uniqBy(data, "producto_id");

    // 2. Calcular la distancia para cada producto
    const dataWithDistance = uniqueData.map((item) => {
      // Asegurarse de que las coordenadas existan en la máquina
      const maquinaLat = item.maquina_latitud;
      const maquinaLon = item.maquina_longitud;

      const distanciaCalculada =
        maquinaLat && maquinaLon
          ? getDistance(maquinaLat, maquinaLon, userLat, userLon)
          : Infinity; // Usar Infinity para enviar productos sin coordenadas al final

      return {
        ...item,
        _distanciaOrdenamiento: distanciaCalculada,
      };
    });

    // 3. ⭐️ Ordenar de menor a mayor distancia
    return dataWithDistance.sort(
      (a, b) => a._distanciaOrdenamiento - b._distanciaOrdenamiento,
    );
  };

  useEffect(() => {
    fetchEmpresas();
  }, [fetchEmpresas]);

  useEffect(() => {
    // Obtener las coordenadas de usuario, priorizando userAddress
    const currentLat =
      userAddress?.length === 2 ? Number(userAddress[0]) : latitude;
    const currentLon =
      userAddress?.length === 2 ? Number(userAddress[1]) : longitude;

    if (userAddress?.length === 2) {
      mutateAsync({ latitud: currentLat, longitud: currentLon })
        .then((data) => {
          // ⭐️ APLICACIÓN 1: Ordenar los resultados del servidor
          const sortedData = processAndSortProducts(
            data,
            currentLat,
            currentLon,
            getDistance,
            _,
          );
          setProductosMasVendidos(sortedData);
        })
        .catch((error) => console.error("Error al cargar productos:", error));
    } else {
      const success = (position) => {
        setLatitude(position.coords.latitude);

        setLongitude(position.coords.longitude);

        setLoading(false);
      };

      // Función para manejar errores al obtener la ubicación
      const geoError = (err) => {
        switch (err.code) {
          case err.PERMISSION_DENIED:
            setErrorGeo(
              "Permiso denegado por el usuario para acceder a la ubicación.",
            );

            break;

          case err.POSITION_UNAVAILABLE:
            setErrorGeo("Información de ubicación no disponible.");

            break;

          case err.TIMEOUT:
            setErrorGeo("La solicitud para obtener la ubicación ha caducado.");

            break;

          case err.UNKNOWN_ERROR:
            setErrorGeo("Un error desconocido ha ocurrido.");

            break;

          default:
            setErrorGeo("Error al obtener la ubicación.");
        }

        setLoading(false);
      };

      // Opciones para la solicitud de ubicación (opcional)
      const options = {
        enableHighAccuracy: true, // Solicita la mejor precisión posible

        timeout: 5000, // Tiempo máximo en milisegundos para obtener la ubicación

        maximumAge: 0, // No usar una posición en caché, siempre obtener una nueva
      };

      navigator.geolocation.getCurrentPosition(success, geoError, options);

      if (latitude && longitude) {
        mutateAsync({ latitud: currentLat, longitud: currentLon })
          .then((data) => {
            // ⭐️ APLICACIÓN 2: Ordenar después de obtener la ubicación por GPS
            const sortedData = processAndSortProducts(
              data,
              currentLat,
              currentLon,
              getDistance,
              _,
            );
            setProductosMasVendidos(sortedData);
          })
          .catch((error) => console.error("Error al cargar productos:", error));
      } else {
        // Coordenadas por defecto
        const defaultLat = -33.43695899222021;
        const defaultLon = -70.63442929100255;

        mutateAsync({ latitud: defaultLat, longitud: defaultLon })
          .then((data) => {
            // ⭐️ APLICACIÓN 3: Ordenar para la ubicación por defecto
            const sortedData = processAndSortProducts(
              data,
              defaultLat,
              defaultLon,
              getDistance,
              _,
            );
            setProductosMasVendidos(sortedData);
          })
          .catch((error) => console.error("Error al cargar productos:", error));
      }
    }
  }, [userAddress, latitude, longitude, mutateAsync]); // Agregué latitude y longitude a las dependencias por seguridad.

  useEffect(() => {
    if (productosMasVendidos) {
      // Si hay datos, actualiza 'allProducts' con los datos obtenidos.
      setAllProducts(productosMasVendidos);

      // Si no hay un filtro de tienda seleccionado, inicializa 'filteredItems' con todos los productos
      if (selectedTiendaId === null) {
        setFilteredItems(productosMasVendidos);
      }
    }
  }, [productosMasVendidos, selectedTiendaId]);

  useEffect(() => {
    if (selectedTiendaId !== null) {
      // Si hay una tienda seleccionada
      const filteredList = allProducts.filter(
        (item) => item.empresa_id === selectedTiendaId,
      );
      setFilteredItems(filteredList);
    } else {
      // Si no hay tienda seleccionada (o se reseteó), se muestran todos los productos
      setFilteredItems(allProducts);
    }
  }, [allProducts, selectedTiendaId]);

  useEffect(() => {
    setComunas(maquinas);
  }, [maquinas]);

  const handleClick = async (item) => {
    if (!item) return;
    const requestId = ++requestIdRef.current;
    itemRef.current = item;
    storeRef.current =
      _.find(Array.isArray(empresasData) ? empresasData : [], {
        empresa_id: item.empresa_id,
      }) ?? null;
    setMaquinas([]);
    setIsLoadingMaquinas(true);
    setShow(true);
    try {
      const resp = await mutateAsyncMaquinas(item.producto_nombre);
      if (requestIdRef.current !== requestId) return;
      setMaquinas(Array.isArray(resp) ? resp : []);
    } catch (err) {
      if (requestIdRef.current !== requestId) return;
      Sentry.captureException(err);
      setMaquinas(null);
    } finally {
      if (requestIdRef.current === requestId) setIsLoadingMaquinas(false);
    }
  };

  const handleClickTienda = (idTienda) => {
    setSelectedTiendaId(idTienda);

    // const filteredList = productos.data.filter((item) =>
    // item.empresa_id == idTienda
    // );
    // setFilteredItems(filteredList);
  };

  const goToHome = () => {
    const homePath = localStorage.getItem("companyHome") || "/";
    navigate(homePath);
  };

  const FeaturedTitle = () => {
    return (
      <div className="featured-title" onClick={goToHome}>
        <i className="arrow-left" />
        <h2>Lo más comprado</h2>
      </div>
    );
  };

  const FeaturedSubtitle = () => {
    return (
      <div className="featured-subtitle">
        <h2>Filtrar por tienda</h2>
      </div>
    );
  };

  const ProductCard = ({ item, index }) => {
    return (
      <div key={index} className="card-product-carousel">
        <div className="card-img-product" onClick={() => handleClick(item)}>
          <img src={item.producto_foto} alt={item.producto_id} />
        </div>

        {/* <ButtonLike /> */}

        <div className="card-content-product">
          <h2>{item.producto_nombre}</h2>
          <p className="price-product">
            {" "}
            {formatMoney(item.producto_precio)} CLP
          </p>
          {/* <div className="location-product">
            <i className="icon-location"></i>
            <span className="text-location">{item.maquina_direccion}</span>
          </div> */}
        </div>
      </div>
    );
  };

  const SwiperStorageAvailable = () => {
    if (_.size(filteredItems) > 0) {
      if (_.size(filteredItems) > 3) {
        return (
          <Swiper
            slidesPerView={3}
            grid={{
              rows: 3,
              fill: "row",
            }}
            spaceBetween={10}
            pagination={{
              clickable: true,
            }}
            modules={[Grid, Pagination]}
            className="mySwiper"
            breakpoints={{
              340: {
                slidesPerView: 42,
                spaceBetween: 20,
              },
              768: {
                slidesPerView: 3,
                spaceBetween: 10,
              },
              1024: {
                slidesPerView: 4,
                spaceBetween: 5,
              },
            }}
          >
            {_.size(filteredItems) > 0 ? (
              filteredItems.map((product) => {
                return (
                  <SwiperSlide key={product.producto_id}>
                    <ProductCard
                      key={product.producto_id}
                      item={{ ...product, like: false }}
                    />
                  </SwiperSlide>
                );
              })
            ) : (
              <Skeleton avatar paragraph={{ rows: 4 }} />
            )}
          </Swiper>
        );
      } else {
        return (
          <Swiper
            slidesPerView={3}
            grid={{
              rows: 1,
              fill: "row",
            }}
            spaceBetween={5}
            pagination={{
              clickable: true,
            }}
            modules={[Grid, Pagination]}
            className="mySwiper"
            breakpoints={{
              340: {
                slidesPerView: 1,
                spaceBetween: 10,
              },
              768: {
                slidesPerView: 2,
                spaceBetween: 10,
              },
              1024: {
                slidesPerView: 3,
                spaceBetween: 5,
              },
            }}
          >
            {_.size(filteredItems) > 0 ? (
              filteredItems.map((product) => {
                return (
                  <SwiperSlide key={product.producto_id}>
                    <ProductCard
                      key={product.producto_id}
                      item={{ ...product, like: false }}
                    />
                  </SwiperSlide>
                );
              })
            ) : (
              <Skeleton avatar paragraph={{ rows: 4 }} />
            )}
          </Swiper>
        );
      }
    } else {
      return (
        <h4 className="text-center prompt-bold text-black mb-3">
          No se encontraron productos disponibles.
        </h4>
      );
    }
  };

  return (
    <div className="container my-5" style={{ flexGrow: 1 }}>
      {show && (
        <ModalProductDetail
          show={show}
          setShow={setShow}
          item={itemRef}
          store={storeRef}
          machines={maquinas}
          isLoading={isLoadingMaquinas}
        />
      )}
      <FeaturedTitle />
      <FeaturedSubtitle />
      <div className="row">
        <div className="col-12">
          <StoresSwiper handleClickTienda={handleClickTienda} />
        </div>
        <div className="col-12">
          {/* <Skeleton loading={productos} active> */}
          {/* <StoreCarousel /> */}
          <SwiperStorageAvailable />
          {/* </Skeleton> */}
        </div>
      </div>
    </div>
  );
};
